'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { TenantBrandHeader } from '@/components/auth/tenant-brand-header';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      // We always show the same confirmation regardless of result
      // — server returns 200 even when email doesn't match a user.
      if (!res.ok) {
        // Network-level failure only (server returns 200 for everything else)
        throw new Error('Request failed');
      }
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <TenantBrandHeader />
        <Card className="w-full">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Reset your password</CardTitle>
          <CardDescription className="text-center">
            Enter the email address associated with your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md text-sm">
                If an account exists for <strong>{email}</strong>, a password reset link has been sent.
                Check your inbox (and spam folder).
              </div>
              <p className="text-sm text-muted-foreground">
                The link expires in 1 hour and can only be used once.
              </p>
              <Link
                href="/login"
                className="block text-center text-sm text-indigo-600 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>
              <Button type="submit" disabled={loading || !email} className="w-full">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Send reset link
              </Button>
              <Link
                href="/login"
                className="block text-center text-sm text-indigo-600 hover:underline"
              >
                Back to sign in
              </Link>
            </form>
          )}
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
